import React, { useState, useEffect, useMemo, useRef } from 'react';
import API from '../../api/axiosInstance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheck, 
  faTimes, 
  faPen,
  faEnvelope,
  faSort,
  faSortUp,
  faSortDown,
  faUndo,
  faRotateLeft,
  faRotateRight,
  faMagnifyingGlassPlus,
  faMagnifyingGlassMinus,
  faCrop,
  faArrowsLeftRight,
  faArrowsUpDown,
  faSquare,
  faExpand
} from '@fortawesome/free-solid-svg-icons';
import { Cropper } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import '../css/ImageApproval.css';
import { getPersistedEmsUploadRef, resolveEmsUploadPublicUrl } from '../../utils/emsPhotoUtils';
import { ACCREDITATION_S3_PREFIX } from '../../constants/accreditationUploads';

const API_URL = process.env.REACT_APP_API_URL || '';
const REASON_OPTIONS = [
  { value: '', label: 'Select reason' },
  // { value: 'approved', label: 'Approved - Meets requirements' },
  { value: 'Poor quality', label: 'Poor quality' },
  { value: 'Wrong size/dimensions', label: 'Wrong size/dimensions' },
  { value: 'Inappropriate content', label: 'Inappropriate content' },
  { value: 'Face not clearly visible', label: 'Face not clearly visible' },
  { value: 'Background is not suitable', label: 'Background not suitable' },
];

const ImageApproval = () => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reasons, setReasons] = useState({});
  const [notes, setNotes] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  //denotes the preview image modal state.
  const [imagePreview, setImagePreview] = useState(null);
  
  // Cropper modal state
  const [editModal, setEditModal] = useState({
    open: false,
    participantId: null,
    photoSrc: null,
    participantName: null
  });
  // Revert confirmation modal: null when closed, or { participantId, participantName } when open
  const [revertConfirm, setRevertConfirm] = useState(null);
  // Approve/Reject confirmation: null when closed, or { participantId, participantName, action: 'approved'|'rejected' }
  const [approveRejectConfirm, setApproveRejectConfirm] = useState(null);
  // Rejection email confirmation: null when closed, or { participantId, participantName }
  const [rejectEmailConfirm, setRejectEmailConfirm] = useState(null);
  const [cropperSaving, setCropperSaving] = useState(false);
  const [cropperError, setCropperError] = useState(null);
  const cropperRef = useRef(null);
  
  // Rotation and zoom state
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  
  // Advanced cropper state
  const [aspectRatio, setAspectRatio] = useState(1); // 1 = 1:1, 4/3, 16/9, null = free
  const [flippedH, setFlippedH] = useState(false);
  const [flippedV, setFlippedV] = useState(false);
  const [activeTool, setActiveTool] = useState('crop'); // 'crop', 'flip', 'ratio'

  const fetchList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get(`/ems/participants/photo-approval/list`, {
        params: { status: 'all' },
      });

      console.log('res.data', res.data);
      setParticipants(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load photos');
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const mappedRows = useMemo(() => {
    return (participants || []).map((p) => {
      const photo0 = p.person_data?.photo?.[0];
      const url = photo0?.url;
      const originalUrl = photo0?.original_url;
      const currentPhotoRef = photo0?.url ?? photo0?.name ?? photo0?.key;
      const resolvedPhotoSrc = resolveEmsUploadPublicUrl(currentPhotoRef, API_URL);
      const photoSrc = resolvedPhotoSrc || '/images/avatar.png';

      // Determine if revert is possible: original_url exists and is different from current url
      const canRevert = Boolean(originalUrl && originalUrl !== url);

      return {
        id: p._id,
        firstName: p.person_id?.name ?? p.person_data?.givenFirstName ?? '',
        lastName: p.person_id?.lname ?? p.person_data?.familyLastName ?? '',
        photo: photoSrc,
        photo_approval_status: photo0?.photo_approval_status || 'pending',
        photo_approval_reason: photo0?.photo_approval_reason ?? '',
        photo_approval_notes: photo0?.photo_approval_notes ?? '',
        original_url: originalUrl,
        canRevert,
      };
    });
  }, [participants]);

  const sortedRows = useMemo(() => {
    const key = sortConfig.key;
    const dir = sortConfig.direction;
    if (!key || !dir) return mappedRows;
    return [...mappedRows].sort((a, b) => {
      const va = (a[key] || '').toLowerCase();
      const vb = (b[key] || '').toLowerCase();
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [mappedRows, sortConfig.key, sortConfig.direction]);

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key 
        ? prev.direction === 'asc' ? 'desc' : 'asc'
        : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleReasonChange = (id, value) => {
    setReasons(prev => ({ ...prev, [id]: value }));
  };

  const handleNotesChange = (id, value) => {
    setNotes(prev => ({ ...prev, [id]: value }));
  };

  const getReasonForRow = (row) => {
    // If user has selected/changed a reason in this session, use that
    if (reasons[row.id] !== undefined) return reasons[row.id];
    // Otherwise, use the saved reason from database
    return row.photo_approval_reason ?? '';
  };

  const getNotesForRow = (row) => {
    // If user has typed/changed notes in this session, use that
    if (notes[row.id] !== undefined) return notes[row.id];
    // Otherwise, use the saved notes from database
    return row.photo_approval_notes ?? '';
  };

  const handlePhotoApproval = async (participantId, status) => {
    const row = mappedRows.find((r) => r.id === participantId);
    const reason = (reasons[participantId] ?? row?.photo_approval_reason) ?? '';
    const notesVal = (notes[participantId] ?? row?.photo_approval_notes) ?? '';

    setUpdatingId(participantId);
    try {
      await API.patch(`/ems/participants/${participantId}/photo-approval`, {
        status,
        reason: reason.trim(),
        notes: String(notesVal).trim(),
      });
      setReasons((r) => ({ ...r, [participantId]: '' }));
      setNotes((n) => ({ ...n, [participantId]: '' }));
      await fetchList();
      // Use global toast (window.alert override in index.js) - top-center, auto-dismiss
      alert(status === 'approved' ? 'Image approved.' : 'Image rejected.', 'success');
    } catch (err) {
      console.error('Photo approval update failed:', err);
      alert(err.response?.data?.message || 'Failed to update photo status', 'danger');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleApprove = (row) => {
    setApproveRejectConfirm({
      participantId: row.id,
      participantName: `${row.firstName} ${row.lastName}`.trim() || 'this participant',
      action: 'approved',
    });
  };

  const handleReject = (row) => {
    setApproveRejectConfirm({
      participantId: row.id,
      participantName: `${row.firstName} ${row.lastName}`.trim() || 'this participant',
      action: 'rejected',
    });
  };

  const handleEdit = (row) => {
    // Open cropper modal with participant photo
    setEditModal({
      open: true,
      participantId: row.id,
      photoSrc: row.photo,
      participantName: `${row.firstName} ${row.lastName}`
    });
    setCropperError(null);
    // Reset all cropper state when opening modal
    setRotation(0);
    setZoom(1);
    setAspectRatio(1);
    setFlippedH(false);
    setFlippedV(false);
    setActiveTool('crop');
  };

  const handleRevert = async (participantId) => {
    setUpdatingId(participantId);
    try {
      await API.patch(`/ems/participants/${participantId}/photo-approval/revert`);
      await fetchList();
    } catch (err) {
      console.error('Photo revert failed:', err);
      alert(err.response?.data?.message || 'Failed to revert photo to original');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCloseEditModal = () => {
    setEditModal({
      open: false,
      participantId: null,
      photoSrc: null,
      participantName: null
    });
    setCropperError(null);
    setCropperSaving(false);
    // Reset all cropper state when closing modal
    setRotation(0);
    setZoom(1);
    setAspectRatio(1);
    setFlippedH(false);
    setFlippedV(false);
    setActiveTool('crop');
  };

  const handleCropperSave = async () => {
    if (!cropperRef.current) {
      setCropperError('Cropper not ready');
      return;
    }

    const canvas = cropperRef.current.getCanvas();
    if (!canvas) {
      setCropperError('Unable to extract image. Please try again.');
      return;
    }

    setCropperSaving(true);
    setCropperError(null);

    try {
      // Step 1: Convert canvas to blob
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to create image blob'));
        }, 'image/png', 1.0);
      });

      // Step 2: Create file from blob
      const file = new File([blob], `profile-photo-${Date.now()}.png`, { type: 'image/png' });

      // Step 3: Upload to server
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dir', ACCREDITATION_S3_PREFIX);

      // Shared instance attaches the Bearer token — /api/ems/uploads requires auth.
      const uploadRes = await API.post(`/ems/uploads`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const persistedPhotoUrl = getPersistedEmsUploadRef(uploadRes.data);

      // Step 5: Get current participant data
      const participantRes = await API.get(`/ems/participants/${editModal.participantId}`);
      const participant = participantRes.data;

      // Step 6: Parse current person_data safely
      let currentPersonData = participant.person_data || {};
      if (typeof currentPersonData === 'string') {
        try {
          currentPersonData = JSON.parse(currentPersonData);
        } catch {
          currentPersonData = {};
        }
      }

      // Step 7: Merge new photo into person_data (keep approval fields; Form.io + participant edit read `photo`)
      const prev0 =
        Array.isArray(currentPersonData.photo) && currentPersonData.photo[0] && typeof currentPersonData.photo[0] === 'object'
          ? currentPersonData.photo[0]
          : {};
      const updatedPersonData = {
        ...currentPersonData,
        photo: [
          {
            ...prev0,
            url: persistedPhotoUrl,
          },
        ],
      };

      // Step 8: Update participant with new photo (admin_edit flag tells backend to preserve original_url)
      await API.put(`/ems/participants/${editModal.participantId}`, {
        person_data: updatedPersonData,
        admin_edit: true
      });

      // Step 9: Success - refresh list and close modal
      await fetchList();
      handleCloseEditModal();

    } catch (err) {
      console.error('Cropper save failed:', err);
      setCropperError(err.response?.data?.message || err.message || 'Failed to save edited photo');
    } finally {
      setCropperSaving(false);
    }
  };

  const handleCropperReset = () => {
    if (cropperRef.current) {
      cropperRef.current.reset();
      setRotation(0);
      setZoom(1);
      setAspectRatio(1);
      setFlippedH(false);
      setFlippedV(false);
      setActiveTool('crop');
    }
  };

  // Rotation handlers
  const handleRotateLeft = () => {
    if (cropperRef.current) {
      cropperRef.current.rotateImage(-90);
      setRotation(prev => {
        const newVal = prev - 90;
        return newVal < 0 ? newVal + 360 : newVal;
      });
    }
  };

  const handleRotateRight = () => {
    if (cropperRef.current) {
      cropperRef.current.rotateImage(90);
      setRotation(prev => (prev + 90) % 360);
    }
  };

  // Zoom handlers
  const handleZoomIn = () => {
    if (cropperRef.current) {
      const newZoom = Math.min(zoom + 0.1, 2.0);
      cropperRef.current.zoomImage(newZoom / zoom); // zoomImage uses relative scale
      setZoom(newZoom);
    }
  };

  const handleZoomOut = () => {
    if (cropperRef.current) {
      const newZoom = Math.max(zoom - 0.1, 0.5);
      cropperRef.current.zoomImage(newZoom / zoom); // zoomImage uses relative scale
      setZoom(newZoom);
    }
  };

  const handleZoomChange = (e) => {
    const newZoom = parseFloat(e.target.value);
    if (cropperRef.current && newZoom !== zoom) {
      cropperRef.current.zoomImage(newZoom / zoom); // zoomImage uses relative scale
      setZoom(newZoom);
    }
  };

  // Flip handlers
  const handleFlipHorizontal = () => {
    if (cropperRef.current) {
      cropperRef.current.flipImage(true, false);
      setFlippedH(prev => !prev);
    }
  };

  const handleFlipVertical = () => {
    if (cropperRef.current) {
      cropperRef.current.flipImage(false, true);
      setFlippedV(prev => !prev);
    }
  };

  // Aspect ratio handler
  const handleAspectRatioChange = (ratio) => {
    setAspectRatio(ratio);
  };

  // Fine rotation handler (continuous rotation)
  const handleFineRotation = (e) => {
    const newRotation = parseFloat(e.target.value);
    if (cropperRef.current) {
      const diff = newRotation - rotation;
      cropperRef.current.rotateImage(diff);
      setRotation(newRotation);
    }
  };

  const handleSendNotification = (row) => {
    // Ensure reason is selected before sending rejection email
    const reason = getReasonForRow(row);
    const notesVal = getNotesForRow(row);
    if (!reason) {
      alert('Select a reason', 'danger');
      return;
    }
    // Open confirmation modal with current reason/notes so they are sent in the email
    setRejectEmailConfirm({
      participantId: row.id,
      participantName: `${row.firstName} ${row.lastName}`.trim() || 'this participant',
      reason,
      notes: notesVal,
    });
  };

  const handleSendRejectionEmail = async (participantId, reason, notes) => {
    setUpdatingId(participantId);
    try {
      const payload = { reason: reason ?? '', notes: notes ?? '' };
      console.log("Sending rejection email payload", payload);
      await API.post(`/ems/participants/${participantId}/photo-approval/send-rejection-email`, payload);
      alert('Rejection email sent', 'success');
    } catch (err) {
      console.error('Send rejection email error:', err);
      alert(err.response?.data?.message || 'Failed to send rejection email', 'danger');
    } finally {
      setUpdatingId(null);
    }
  };

  const isApproved = (row) => row.photo_approval_status === 'approved';
  const isRejected = (row) => row.photo_approval_status === 'rejected';
  const isPending = (row) => row.photo_approval_status === 'pending' || !row.photo_approval_status;

  return (
    <div>
      {/* Title and Description */}
      <div style={{ marginTop: '82px', marginBottom: '20px' }}>
        <h1>Photo Approval Dashboard</h1>
        <p className="ems-location-info">
          Review and approve or reject submitted profile photos
        </p>
      </div>

      {/* Table Container */}
      <div className="image-approval-table-container">
        {/* Table Header */}
        <div className="image-approval-header">
          <div className="image-approval-col col-image">Image</div>
          <div 
            className={`image-approval-col col-name sort-header ${sortConfig.key === 'firstName' ? 'sorted' : ''}`}
            onClick={() => toggleSort('firstName')}
          >
            <span>First Name</span>
            <FontAwesomeIcon icon={sortIcon('firstName')} className="sort-icon" />
          </div>
          <div 
            className={`image-approval-col col-name sort-header ${sortConfig.key === 'lastName' ? 'sorted' : ''}`}
            onClick={() => toggleSort('lastName')}
          >
            <span>Last Name</span>
            <FontAwesomeIcon icon={sortIcon('lastName')} className="sort-icon" />
          </div>
          <div className="image-approval-col col-actions">Actions</div>
          <div className="image-approval-col col-reason">Reason</div>
          <div className="image-approval-col col-notes">Notes</div>
          <div className="image-approval-col col-notify"></div>
        </div>

        {/* Table Rows */}
        {loading ? (
          <p className="text-muted p-2">Loading...</p>
        ) : error ? (
          <p className="text-danger p-2">{error}</p>
        ) : sortedRows.length === 0 ? (
          <p className="text-muted p-2">No photos with approval status</p>
        ) : (
          sortedRows.map((row) => (
            <div
              key={row.id}
              className={`image-approval-row image-approval-row--${row.photo_approval_status || 'pending'}`}
            >
              {/* Image Column - click to open preview modal */}
              <div className="image-approval-col col-image">
                <button
                  type="button"
                  className="image-approval-photo-trigger"
                  onClick={() => setImagePreview({ src: row.photo, alt: `${row.firstName} ${row.lastName}` })}
                  title="View full size"
                >
                  <img 
                    src={row.photo} 
                    alt={`${row.firstName} ${row.lastName}`}
                    className="profile-image-circle"
                    onError={(e) => {
                      e.target.src = '/images/avatar.png';
                    }}
                  />
                </button>
              </div>

              {/* First Name / Last Name */}
              <div className="image-approval-col col-name">{row.firstName}</div>
              <div className="image-approval-col col-name">{row.lastName}</div>

              {/* Actions Column: always both tick and cross; color indicates current state (green = approved, red = rejected, black = pending) */}
              <div className="image-approval-col col-actions">
                <div className="image-approval-actions-inner">
                  <button
                    type="button"
                    className={`action-btn ${isApproved(row) ? 'action-btn--approved' : 'action-btn--pending'}`}
                    title="Approve"
                    onClick={() => handleApprove(row)}
                    disabled={updatingId === row.id}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                  <button
                    type="button"
                    className={`action-btn ${isRejected(row) ? 'action-btn--rejected' : 'action-btn--pending'}`}
                    title="Reject"
                    onClick={() => handleReject(row)}
                    disabled={updatingId === row.id}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                  <button
                    type="button"
                    className="action-btn action-btn--edit"
                    title="Edit"
                    onClick={() => handleEdit(row)}
                    disabled={updatingId === row.id}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button
                    type="button"
                    className={`action-btn action-btn--revert ${!row.canRevert ? 'action-btn--disabled' : ''}`}
                    title={row.canRevert ? "Revert to original" : "No original to revert to"}
                    onClick={() => setRevertConfirm({ participantId: row.id, participantName: `${row.firstName} ${row.lastName}`.trim() || 'this participant' })}
                    disabled={updatingId === row.id || !row.canRevert}
                  >
                    <FontAwesomeIcon icon={faUndo} />
                  </button>
                </div>
              </div>

              {/* Reason Column: disabled when approved */}
              <div className="image-approval-col col-reason">
                <select
                  className="reason-select"
                  value={getReasonForRow(row)}
                  onChange={(e) => handleReasonChange(row.id, e.target.value)}
                  disabled={!isRejected(row)}
                >
                  {REASON_OPTIONS.map((opt) => (
                    <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Notes Column: disabled when approved */}
              <div className="image-approval-col col-notes">
                <input
                  type="text"
                  className="notes-input"
                  placeholder="Type..."
                  value={getNotesForRow(row)}
                  onChange={(e) => handleNotesChange(row.id, e.target.value)}
                  disabled={!isRejected(row)}
                />
              </div>

              {/* Notification Column */}
              <div className="image-approval-col col-notify">
                <button
                  className="notify-btn"
                  onClick={() => handleSendNotification(row)}
                  title={isRejected(row) ? "Send rejection email" : "Only available when photo is rejected"}
                  disabled={!isRejected(row)}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Image preview modal */}
      {imagePreview && (
        <div
          className="image-approval-modal-backdrop"
          onClick={() => setImagePreview(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
        >
          <div
            className="image-approval-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="image-approval-modal-close"
              onClick={() => setImagePreview(null)}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <img
              src={imagePreview.src}
              alt={imagePreview.alt}
              className="image-approval-modal-img"
              onError={(e) => {
                e.target.src = '/images/avatar.png';
              }}
            />
            {imagePreview.alt && (
              <p className="image-approval-modal-caption">{imagePreview.alt}</p>
            )}
          </div>
        </div>
      )}

      {/* Cropper modal for editing photos */}
      {editModal.open && (
        <div
          className="image-approval-modal-backdrop"
          onClick={handleCloseEditModal}
          role="dialog"
          aria-modal="true"
          aria-label="Edit photo"
        >
          <div
            className="image-cropper-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="image-cropper-modal-header">
              <h3 className="image-cropper-modal-title">
                Edit Photo {editModal.participantName && `- ${editModal.participantName}`}
              </h3>
              <button
                type="button"
                className="image-approval-modal-close"
                onClick={handleCloseEditModal}
                aria-label="Close"
                disabled={cropperSaving}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Main Content: Sidebar + Cropper */}
            <div className="image-cropper-main">
              {/* Left Sidebar */}
              <div className="image-cropper-sidebar">
                <button
                  className={`sidebar-tool ${activeTool === 'crop' ? 'active' : ''}`}
                  onClick={() => setActiveTool('crop')}
                  title="Crop"
                  disabled={cropperSaving}
                >
                  <FontAwesomeIcon icon={faCrop} />
                </button>
                <button
                  className={`sidebar-tool ${activeTool === 'flip' ? 'active' : ''}`}
                  onClick={() => setActiveTool('flip')}
                  title="Flip"
                  disabled={cropperSaving}
                >
                  <FontAwesomeIcon icon={faArrowsLeftRight} />
                </button>
                <button
                  className={`sidebar-tool ${activeTool === 'ratio' ? 'active' : ''}`}
                  onClick={() => setActiveTool('ratio')}
                  title="Aspect Ratio"
                  disabled={cropperSaving}
                >
                  <FontAwesomeIcon icon={faSquare} />
                </button>
              </div>

              {/* Cropper Container */}
              <div className="image-cropper-container">
                <Cropper
                  ref={cropperRef}
                  src={editModal.photoSrc}
                  className="image-cropper"
                  stencilProps={{
                    aspectRatio: aspectRatio // Now dynamic
                  }}
                />

                {/* Tool-specific panel overlay */}
                {activeTool === 'flip' && (
                  <div className="tool-panel flip-panel">
                    <button
                      className={flippedH ? 'active' : ''}
                      onClick={handleFlipHorizontal}
                      disabled={cropperSaving}
                    >
                      <FontAwesomeIcon icon={faArrowsLeftRight} />
                      <span>Horizontal</span>
                    </button>
                    <button
                      className={flippedV ? 'active' : ''}
                      onClick={handleFlipVertical}
                      disabled={cropperSaving}
                    >
                      <FontAwesomeIcon icon={faArrowsUpDown} />
                      <span>Vertical</span>
                    </button>
                  </div>
                )}

                {activeTool === 'ratio' && (
                  <div className="tool-panel ratio-panel">
                    <button
                      className={aspectRatio === 1 ? 'active' : ''}
                      onClick={() => handleAspectRatioChange(1)}
                      disabled={cropperSaving}
                    >
                      1:1
                    </button>
                    <button
                      className={aspectRatio === 4/3 ? 'active' : ''}
                      onClick={() => handleAspectRatioChange(4/3)}
                      disabled={cropperSaving}
                    >
                      4:3
                    </button>
                    <button
                      className={aspectRatio === 16/9 ? 'active' : ''}
                      onClick={() => handleAspectRatioChange(16/9)}
                      disabled={cropperSaving}
                    >
                      16:9
                    </button>
                    <button
                      className={aspectRatio === null ? 'active' : ''}
                      onClick={() => handleAspectRatioChange(null)}
                      disabled={cropperSaving}
                    >
                      <FontAwesomeIcon icon={faExpand} />
                      <span>Free</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Toolbar: Fine Rotation & Quick Controls */}
            <div className="image-cropper-toolbar">
              {/* Quick Flip Button */}
              <button
                type="button"
                className="toolbar-btn"
                onClick={handleFlipHorizontal}
                disabled={cropperSaving}
                title="Flip Horizontal"
              >
                <FontAwesomeIcon icon={faArrowsLeftRight} />
              </button>

              {/* Rotate 90° Buttons */}
              <button
                type="button"
                className="toolbar-btn"
                onClick={handleRotateLeft}
                disabled={cropperSaving}
                title="Rotate Left (-90°)"
              >
                <FontAwesomeIcon icon={faRotateLeft} />
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={handleRotateRight}
                disabled={cropperSaving}
                title="Rotate Right (+90°)"
              >
                <FontAwesomeIcon icon={faRotateRight} />
              </button>

              {/* Fine Rotation Slider */}
              <div className="toolbar-group fine-rotation-group">
                <input
                  type="range"
                  min="-45"
                  max="45"
                  step="1"
                  value={rotation > 180 ? rotation - 360 : rotation}
                  onChange={handleFineRotation}
                  className="fine-rotation-slider"
                  disabled={cropperSaving}
                />
                <span className="toolbar-value">{rotation}°</span>
              </div>

              {/* Zoom Controls */}
              <div className="toolbar-group">
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={handleZoomOut}
                  disabled={cropperSaving || zoom <= 0.5}
                  title="Zoom Out"
                >
                  <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
                </button>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={zoom}
                  onChange={handleZoomChange}
                  className="toolbar-slider"
                  disabled={cropperSaving}
                />
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={handleZoomIn}
                  disabled={cropperSaving || zoom >= 2.0}
                  title="Zoom In"
                >
                  <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                </button>
                <span className="toolbar-value">{Math.round(zoom * 100)}%</span>
              </div>

              {/* Reset Button */}
              <button
                type="button"
                className="toolbar-btn"
                onClick={handleCropperReset}
                disabled={cropperSaving}
                title="Reset"
              >
                <FontAwesomeIcon icon={faUndo} />
              </button>
            </div>

            {/* Error message */}
            {cropperError && (
              <div className="image-cropper-error">
                {cropperError}
              </div>
            )}

            {/* Modal Footer */}
            <div className="image-cropper-modal-footer">
              <div className="image-cropper-footer-actions">
                <button
                  type="button"
                  className="image-cropper-btn image-cropper-btn--cancel"
                  onClick={handleCloseEditModal}
                  disabled={cropperSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="image-cropper-btn image-cropper-btn--save"
                  onClick={handleCropperSave}
                  disabled={cropperSaving}
                >
                  {cropperSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve/Reject confirmation modal */}
      {approveRejectConfirm && (
        <div
          className="image-approval-modal-backdrop"
          onClick={() => setApproveRejectConfirm(null)}
          role="dialog"
          aria-modal="true"
          aria-label={approveRejectConfirm.action === 'approved' ? 'Confirm approve' : 'Confirm reject'}
        >
          <div
            className="image-cropper-modal-content image-approval-revert-modal-content image-approval-approve-reject-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="image-cropper-modal-header">
              <h3 className="image-cropper-modal-title">
                {approveRejectConfirm.action === 'approved' ? 'Confirm to approve this photo?' : 'Confirm to reject this photo?'}
                {approveRejectConfirm.participantName ? ` - ${approveRejectConfirm.participantName}` : ''}
              </h3>
              <button
                type="button"
                className="image-approval-modal-close"
                onClick={() => setApproveRejectConfirm(null)}
                aria-label="Close"
                disabled={updatingId === approveRejectConfirm.participantId}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="image-approval-revert-modal-body">
              {approveRejectConfirm.action === 'approved'
                ? 'This will mark the photo as approved. Continue?'
                : 'This will mark the photo as rejected. The participant may need to upload a new photo. Continue?'}
            </div>
            <div className="image-cropper-modal-footer">
              <div className="image-cropper-footer-actions">
                <button
                  type="button"
                  className="image-cropper-btn image-cropper-btn--cancel"
                  onClick={() => setApproveRejectConfirm(null)}
                  disabled={updatingId === approveRejectConfirm.participantId}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="image-cropper-btn image-cropper-btn--save"
                  onClick={async () => {
                    try {
                      await handlePhotoApproval(approveRejectConfirm.participantId, approveRejectConfirm.action);
                      setApproveRejectConfirm(null);
                    } catch (_) {
                      // handlePhotoApproval shows toast on error
                    }
                  }}
                  disabled={updatingId === approveRejectConfirm.participantId}
                >
                  {updatingId === approveRejectConfirm.participantId
                    ? (approveRejectConfirm.action === 'approved' ? 'Approving...' : 'Rejecting...')
                    : (approveRejectConfirm.action === 'approved' ? 'Approve' : 'Reject')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revert confirmation modal */}
      {revertConfirm && (
        <div
          className="image-approval-modal-backdrop"
          onClick={() => setRevertConfirm(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Revert to original photo"
        >
          <div
            className="image-cropper-modal-content image-approval-revert-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="image-cropper-modal-header">
              <h3 className="image-cropper-modal-title">
                Revert to original photo?{revertConfirm.participantName ? ` - ${revertConfirm.participantName}` : ''}
              </h3>
              <button
                type="button"
                className="image-approval-modal-close"
                onClick={() => setRevertConfirm(null)}
                aria-label="Close"
                disabled={updatingId === revertConfirm.participantId}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="image-approval-revert-modal-body">
              This will restore the photo to the original uploaded image. Continue?
            </div>
            <div className="image-cropper-modal-footer">
              <div className="image-cropper-footer-actions">
                <button
                  type="button"
                  className="image-cropper-btn image-cropper-btn--cancel"
                  onClick={() => setRevertConfirm(null)}
                  disabled={updatingId === revertConfirm.participantId}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="image-cropper-btn image-cropper-btn--save"
                  onClick={async () => {
                    try {
                      await handleRevert(revertConfirm.participantId);
                      setRevertConfirm(null);
                    } catch (_) {
                      // handleRevert already shows alert on error
                    }
                  }}
                  disabled={updatingId === revertConfirm.participantId}
                >
                  {updatingId === revertConfirm.participantId ? 'Reverting...' : 'Revert'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Email Confirmation Modal */}
      {rejectEmailConfirm && (
        <div
          className="image-approval-modal-backdrop"
          onClick={() => setRejectEmailConfirm(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Confirm rejection email"
        >
          <div
            className="image-cropper-modal-content image-approval-revert-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="image-cropper-modal-header">
              <h3 className="image-cropper-modal-title">
                Confirm to send rejection mail to the user
                {rejectEmailConfirm.participantName ? ` - ${rejectEmailConfirm.participantName}` : ''}
              </h3>
              <button
                type="button"
                className="image-approval-modal-close"
                onClick={() => setRejectEmailConfirm(null)}
                aria-label="Close"
                disabled={updatingId === rejectEmailConfirm.participantId}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="image-approval-revert-modal-body">
              This will send a rejection email with the reason and notes to the participant. Continue?
            </div>
            <div className="image-cropper-modal-footer">
              <div className="image-cropper-footer-actions">
                <button
                  type="button"
                  className="image-cropper-btn image-cropper-btn--cancel"
                  onClick={() => setRejectEmailConfirm(null)}
                  disabled={updatingId === rejectEmailConfirm.participantId}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="image-cropper-btn image-cropper-btn--save"
                  onClick={async () => {
                    try {
                      await handleSendRejectionEmail(
                        rejectEmailConfirm.participantId,
                        rejectEmailConfirm.reason,
                        rejectEmailConfirm.notes
                      );
                      setRejectEmailConfirm(null);
                    } catch (_) {
                      // handleSendRejectionEmail already shows alert on error
                    }
                  }}
                  disabled={updatingId === rejectEmailConfirm.participantId}
                >
                  {updatingId === rejectEmailConfirm.participantId ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageApproval;
